import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";

/**
 * Shared plumbing for `postgres_changes` subscriptions.
 *
 * Generalised out of the documents subscription, which was the only place in
 * the app that handled a re-join correctly. Everything new subscribes through
 * here so the "no replay" recovery is not re-derived (or forgotten) per caller.
 */

export type PgChangeEvent = "INSERT" | "UPDATE" | "DELETE" | "*";

export type PgChangePayload = RealtimePostgresChangesPayload<Record<string, unknown>>;

export interface ResyncChannelBinding {
  event: PgChangeEvent;
  /** Defaults to "public". */
  schema?: string;
  table: string;
  /** PostgREST filter, e.g. `building_id=eq.7`. Omit to receive every row. */
  filter?: string;
  /**
   * Optional. Most subscribers only need to know that *something* changed and
   * react through `onResync` or their own coalesced refetch.
   *
   * Only `documents`, `building_data` and `buildings` are set to
   * REPLICA IDENTITY FULL. On every other published table `payload.old` is
   * `{ id }` on DELETE and `{}` on UPDATE, so a handler there must treat the
   * event as a bare signal and re-read from the database.
   */
  handler?: (payload: PgChangePayload) => void | Promise<void>;
}

export interface ResyncChannelOptions {
  /**
   * Topic label, for readability in the devtools — build it with
   * `realtimeTopic`. It need not be unique: what reaches Supabase carries an
   * instance suffix (see `createResyncChannel`), so two consumers sharing a
   * label still get two independent channels.
   */
  topic: string;
  bindings: ResyncChannelBinding[];
  /**
   * Fired on every SUBSCRIBED *after the first*, i.e. whenever a re-join has
   * just closed a gap the caller never saw.
   */
  onResync?: () => void;
  /**
   * Every status, with the error object Supabase passes alongside the failing
   * ones. Those are logged whether or not a caller listens.
   */
  onStatus?: (status: string, err?: Error) => void;
}

export interface ResyncChannelHandle {
  channel: RealtimeChannel;
  /** Idempotent — safe to call twice and safe to call after an error. */
  unsubscribe: () => void;
}

/**
 * Every handle that has not been torn down yet.
 *
 * `supabase.removeAllChannels()` leaves a channel joined nowhere but still
 * believed live by whoever holds its handle: the latch below stays unset, so a
 * late status calls back into a consumer that is gone. Logout therefore sweeps
 * through here first — see `teardownAllResyncChannels`.
 */
const liveHandles = new Set<ResyncChannelHandle>();

/**
 * Leave every channel opened through this module, latches and all.
 *
 * For app-wide teardown (logout), where the alternative — removing the channels
 * behind the handles' backs — leaves each consumer believing it is subscribed.
 */
export function teardownAllResyncChannels(): void {
  // Copied: `unsubscribe` mutates the set.
  for (const handle of [...liveHandles]) {
    // Guarded per handle: this sweep exists so no channel outlives the session,
    // and one throwing teardown aborting it would leave every handle after it
    // joined — the exact state it is here to prevent.
    try {
      handle.unsubscribe();
    } catch (e) {
      console.error("[realtime] failed to tear down channel", e);
    }
  }
}

/**
 * The numeric id behind a `column=eq.N` filter, or null when the caller has
 * nothing to subscribe to yet.
 *
 * Truthiness plus `Number.isFinite` is not enough: `Number(" ")` and
 * `Number(null)` are both 0 and `" "` is truthy, so a whitespace route param
 * subscribes as `eq.0` — a filter that matches no row, on a channel that joins
 * happily and leaves every cache behind it frozen with nothing on screen to
 * say so. Ids in this schema are positive integers, so anything else is a
 * caller that is not ready rather than a scope to listen to.
 */
export function realtimeScopeId(value: string | number | null | undefined): number | null {
  const numeric = typeof value === "string" ? Number(value.trim()) : Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
}

/**
 * Build a channel topic from scope parts. Nullish parts are skipped so an
 * optional scope (a project id, say) does not produce a `topic:undefined`
 * that silently differs from the unscoped one.
 */
export function realtimeTopic(...parts: Array<string | number | null | undefined>): string {
  return parts.filter((part) => part !== null && part !== undefined && part !== "").join(":");
}

/**
 * Supabase keys its channel registry by topic — `RealtimeClient.channel` hands
 * back the channel already registered under one — and `removeChannel` detaches
 * it only after the async leave roundtrip. A remount of the same consumer (a
 * route walked back and forth, StrictMode's double effect) would therefore be
 * handed a channel still in `leaving` state: `subscribe()` returns early on
 * anything but a `closed` channel, and the bindings are appended a second time,
 * giving either a silently dead surface or a server/client binding mismatch.
 *
 * So the caller's topic is only a label, and each channel gets its own
 * instance suffix — a remount can never collide with the channel it replaces.
 */
let channelSeq = 0;

/**
 * Register one binding.
 *
 * `.on("postgres_changes", …)` is four overloads, one per event literal, so a
 * filter built from a union-typed `event` matches none of them. Switching here
 * hands each overload the literal it wants and keeps the whole module free of
 * casts. Every branch is the same call; only the literal differs.
 */
function bind(
  channel: RealtimeChannel,
  binding: ResyncChannelBinding,
  deliver: (payload: PgChangePayload) => void
): RealtimeChannel {
  const scope = {
    schema: binding.schema ?? "public",
    table: binding.table,
    ...(binding.filter ? { filter: binding.filter } : {}),
  };
  switch (binding.event) {
    case "INSERT":
      return channel.on("postgres_changes", { ...scope, event: "INSERT" }, deliver);
    case "UPDATE":
      return channel.on("postgres_changes", { ...scope, event: "UPDATE" }, deliver);
    case "DELETE":
      return channel.on("postgres_changes", { ...scope, event: "DELETE" }, deliver);
    default:
      return channel.on("postgres_changes", { ...scope, event: "*" }, deliver);
  }
}

/**
 * Open a `postgres_changes` channel that reports its own gaps.
 *
 * postgres_changes does not replay: anything that changed while the socket was
 * down is lost, silently, forever. Any cache fed by these events therefore
 * needs its own way back to the database, and `onResync` is the signal that it
 * is time to take it. The first SUBSCRIBED is the initial join (the caller has
 * just fetched); every later one closed a gap.
 *
 * A background tab makes this the common case rather than the rare one:
 * browsers throttle timers to roughly once a minute, which stalls the
 * realtime client's 25s heartbeat until the socket times out.
 */
export function createResyncChannel(opts: ResyncChannelOptions): ResyncChannelHandle {
  const { topic, bindings, onResync, onStatus } = opts;

  let hasJoined = false;
  // Latches on teardown. `removeChannel` is async, so a status can still land
  // after the caller has unsubscribed; without this a late SUBSCRIBED would
  // fire onResync into an unmounted consumer.
  let torn = false;

  const instanceTopic = `${topic}#${++channelSeq}`;

  let channel = supabase.channel(instanceTopic);

  for (const binding of bindings) {
    const deliver = (payload: PgChangePayload) => {
      if (torn) return;
      // Guarded here, in the shared path, because every consumer routes through
      // it and this runs inside supabase's own socket dispatch: a throw escapes
      // into the socket callback, and an async handler (DocumentStore's
      // `onInsert`) rejects unhandled — the row dropped with nothing logged.
      try {
        const result = binding.handler?.(payload);
        if (result instanceof Promise) {
          result.catch((e) => {
            console.error("[realtime] handler rejected", instanceTopic, binding.table, e);
          });
        }
      } catch (e) {
        console.error("[realtime] handler threw", instanceTopic, binding.table, e);
      }
    };
    channel = bind(channel, binding, deliver);
  }

  // Same reason as the binding guard: this runs inside supabase's subscribe
  // callback, so a consumer throwing here escapes into the socket dispatch.
  const guarded = (what: string, call: () => void) => {
    try {
      call();
    } catch (e) {
      // `what` stays an argument rather than being interpolated: the format
      // string is a literal, which is what the static analysis asks for.
      console.error("[realtime] callback threw", what, instanceTopic, e);
    }
  };

  channel.subscribe((status: string, err?: Error) => {
    if (torn) return;
    // Logged by default rather than behind an opt-in `onStatus`: a channel that
    // never joins is otherwise completely silent, and every caller would have
    // to remember to ask.
    if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
      console.error("[realtime] channel status", instanceTopic, status, err);
    }
    guarded("onStatus", () => onStatus?.(status, err));
    if (status !== "SUBSCRIBED") return;
    // Guarded separately: a throwing onStatus must not cost the resync.
    if (hasJoined) guarded("onResync", () => onResync?.());
    hasJoined = true;
  });

  const handle: ResyncChannelHandle = {
    channel,
    unsubscribe: () => {
      if (torn) return;
      torn = true;
      liveHandles.delete(handle);
      supabase
        .removeChannel(channel)
        // Reported from the RESOLVED value: `removeChannel` resolves with
        // 'ok' | 'timed out' | 'error' and does not reject, so a failed leave
        // is silent unless the status is read.
        .then((status) => {
          if (status !== "ok") {
            console.error("[realtime] channel did not leave cleanly", instanceTopic, status);
          }
        })
        // Kept for the throw a leave can still make (the socket's own state):
        // `void` would make it an unhandled rejection instead.
        .catch((e) => {
          console.error("[realtime] failed to leave channel", instanceTopic, e);
        });
    },
  };
  liveHandles.add(handle);
  return handle;
}
